"use client";

import { useCallback, useId, useState } from "react";
import Modal from "@/components/Modal";
import { ANALYTICS_EVENT, analytics, getAttribution } from "@/tools/analytics";
import Text from "@/components/Text";
import Icon from "@/components/Icon";
import Form from "@/components/Form";
import Field from "@/components/Field";
import { RECAPTCHA_ACTIONS } from "@/tools/helpers/recaptchaActions";
import { getRecaptchaToken } from "@/tools/helpers/recaptcha";
import classNames from "@/helpers/classNames";
import styles from "./styles.module.scss";

export interface NewsletterModalProps {
  show: boolean;
  onClose: (show: boolean) => void;
  title?: string;
  description?: string;
  submitText?: string;
  successTitle?: string;
  successMessage?: string;
  className?: string;
}

interface NewsletterFormValues {
  firstname?: string;
  lastname?: string;
  email: string;
}

const DEFAULT_TITLE = "Sign up to our mailing list";
const DEFAULT_SUBMIT_TEXT = "Receive our emails";
const DEFAULT_SUCCESS_TITLE = "You're signed up!";
const DEFAULT_SUCCESS_MESSAGE =
  "Thanks for joining our mailing list. Keep an eye on your inbox — we'll be in touch soon.";

const NewsletterModal = (props: NewsletterModalProps) => {
  const { show, onClose, title, description, submitText, successTitle, successMessage, className } =
    props;

  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const titleId = useId();

  // The shared Modal doesn't move focus, so a keyboard or screen-reader user
  // would otherwise stay parked on the footer button while the dialog is open.
  // Done through a callback ref rather than an effect on `show` because Modal
  // is a `ssr: false` dynamic import — on the first open the panel isn't in the
  // DOM yet when an effect keyed on `show` would fire.
  const dialogRef = useCallback((node: HTMLDivElement | null) => {
    node?.focus();
  }, []);

  const handleClose = useCallback(
    (value: boolean) => {
      onClose(value);
      // Reset after the close animation so the supporter doesn't watch the
      // success view swap back to the form on the way out.
      setTimeout(() => {
        setIsSuccess(false);
        setSubmitError(null);
      }, 300);
    },
    [onClose],
  );

  const handleSubmit = useCallback(async (values: NewsletterFormValues) => {
    setSubmitError(null);

    // Minted before the request so a rejection is reported as a verification
    // problem rather than a generic failure. Resolves to undefined when no site
    // key is configured, which keeps local and preview environments working.
    let recaptchaToken: string | undefined;
    try {
      recaptchaToken = await getRecaptchaToken(RECAPTCHA_ACTIONS.newsletterSubscribe);
    } catch (error) {
      console.error("[NewsletterModal] reCAPTCHA failed:", error);
      setSubmitError("We couldn't verify your request. Please refresh and try again.");
      return;
    }

    try {
      const res = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recaptchaToken,
          firstname: values.firstname?.trim(),
          lastname: values.lastname?.trim(),
          email: values.email.trim(),
          source: "footer_modal",
        }),
      });
      if (!res.ok) {
        throw new Error(`Request failed with ${res.status}`);
      }
      setIsSuccess(true);
      /**
       * `signup_source` has been a captured attribution key since MAM-462 with
       * nothing ever emitting it — this is the surface it was captured for.
       */
      analytics.track({
        event: ANALYTICS_EVENT.SIGN_UP,
        payload: {
          method: "newsletter",
          signup_source: getAttribution().signup_source,
        },
      });
    } catch (error) {
      console.error("[NewsletterModal] submit failed:", error);
      setSubmitError("Something went wrong signing you up. Please try again in a moment.");
    }
  }, []);

  return (
    <Modal
      hasInnerDialog
      show={show}
      onClose={handleClose}
      modalClassName={styles.modal}
      wrapperClassName={styles.wrapper}
      className={classNames(styles.dialog, className)}
    >
      <div
        ref={dialogRef}
        className={styles.inner}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className={styles.header}>
          <div className={styles.headerTop}>
            <Text
              as="h2"
              id={titleId}
              className={styles.title}
              text={isSuccess ? successTitle || DEFAULT_SUCCESS_TITLE : title || DEFAULT_TITLE}
              variant="heading"
              size="sm"
              color="themeFgDefault"
            />
            <button
              type="button"
              className={styles.close}
              onClick={() => handleClose(false)}
              aria-label="Close"
            >
              <Icon title="close" size="sm" className={styles.closeIcon} />
            </button>
          </div>

          {isSuccess
            ? (successMessage || DEFAULT_SUCCESS_MESSAGE) && (
                <Text
                  className={styles.description}
                  text={successMessage || DEFAULT_SUCCESS_MESSAGE}
                  variant="label"
                  size="md"
                  color="themeFgDefault"
                />
              )
            : description && (
                <Text
                  className={styles.description}
                  text={description}
                  variant="label"
                  size="md"
                  color="themeFgDefault"
                />
              )}
        </header>

        {!isSuccess && (
          <Form
            className={styles.form}
            onSubmit={handleSubmit as never}
            submitButton={{
              text: submitText || DEFAULT_SUBMIT_TEXT,
              hideIcon: true,
              className: styles.submitButton,
            }}
          >
            <Field.Text label="First name" name="firstname" align="left" required hideRequired />
            <Field.Text label="Last name" name="lastname" align="right" required hideRequired />
            <Field.Email label="Email address" name="email" required hideRequired />
            {submitError && (
              <div className={styles.formError} role="alert">
                <Text text={submitError} variant="label" size="md" color="emergencyRed" />
              </div>
            )}
          </Form>
        )}
      </div>
    </Modal>
  );
};

export default NewsletterModal;
